import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth-store';
import { authApi } from '../../api/auth.api';
import { NotificationBanner } from '../../components/NotificationBanner';
import { Lock, Mail, Loader2, Eye, EyeOff } from 'lucide-react';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading } = useAuthStore();
  // Anti open-redirect : seuls les chemins INTERNES sont acceptés ("/..." mais
  // pas "//host" ni "https://..."), sinon on retombe sur le dashboard.
  const rawReturnUrl = searchParams.get('returnUrl');
  const returnUrl =
    rawReturnUrl && rawReturnUrl.startsWith('/') && !rawReturnUrl.startsWith('//')
      ? rawReturnUrl
      : null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Effet pur : redirection si déjà authentifié (autorisé par CONETEXT.md)
  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnUrl || '/dashboard');
    }
  }, [isAuthenticated, navigate, returnUrl]);

  const displayError = localError;

  const handleInputChange = (
    setter: (value: string) => void,
    value: string,
  ) => {
    if (localError) {
      setLocalError(null);
    }
    setter(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!email || !password) {
      setLocalError('Veuillez remplir tous les champs');
      return;
    }

    if (!email.includes('@')) {
      setLocalError('Email invalide');
      return;
    }

    if (password.length < 6) {
      setLocalError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      await authApi.login({ email, password });
      navigate(returnUrl || '/dashboard');
    } catch (err: any) {
      setLocalError(err?.response?.data?.message || 'Identifiants incorrects');
    }
  };

  return (
    <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gradient-to-br tw-from-affinicar-secondary tw-to-affinicar-dark tw-p-4">
      <div className="tw-w-full tw-max-w-md">
        {/* Card */}
        <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-overflow-hidden">
          {/* Header */}
          <div className="tw-bg-gradient-to-r tw-from-affinicar-primary tw-to-affinicar-secondary tw-p-8 tw-text-center">
            <div className="tw-mx-auto tw-mb-4">
              <img 
                src="/assets/images/affinicar/logo_full_white.png" 
                alt="Affinicar"
                className="tw-h-16 tw-w-auto tw-mx-auto"
              />
            </div>
            <p className="tw-text-white tw-text-opacity-90">
              Connectez-vous à votre espace professionnel
            </p>
          </div>

          {/* Form */}
          <div className="tw-p-8">
            {displayError && (
              <NotificationBanner
                type="error"
                message={displayError}
                onClose={() => setLocalError(null)}
              />
            )}

            <form onSubmit={handleSubmit} className="tw-space-y-6">
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2"
                >
                  Adresse email
                </label>
                <div className="tw-relative">
                  <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-pl-3 tw-flex tw-items-center tw-pointer-events-none">
                    <Mail className="tw-w-5 tw-h-5 tw-text-gray-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => handleInputChange(setEmail, e.target.value)}
                    className="tw-block tw-w-full tw-pl-10 tw-pr-3 tw-py-3 tw-border tw-border-gray-300 tw-rounded-lg tw-focus:ring-2 tw-focus:ring-affinicar-primary tw-focus:border-affinicar-primary tw-transition-colors"
                    placeholder="votre.email@exemple.com"
                    disabled={isLoading}
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label
                  htmlFor="password"
                  className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2"
                >
                  Mot de passe
                </label>
                <div className="tw-relative">
                  <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-pl-3 tw-flex tw-items-center tw-pointer-events-none">
                    <Lock className="tw-w-5 tw-h-5 tw-text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) =>
                      handleInputChange(setPassword, e.target.value)
                    }
                    className="tw-block tw-w-full tw-pl-10 tw-pr-10 tw-py-3 tw-border tw-border-gray-300 tw-rounded-lg tw-focus:ring-2 tw-focus:ring-affinicar-primary tw-focus:border-affinicar-primary tw-transition-colors"
                    placeholder="••••••••"
                    disabled={isLoading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="tw-absolute tw-inset-y-0 tw-right-0 tw-pr-3 tw-flex tw-items-center tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="tw-w-5 tw-h-5" />
                    ) : (
                      <Eye className="tw-w-5 tw-h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Forgot Password Link */}
              <div className="tw-text-right">
                <Link
                  to="/forgot-password"
                  className="tw-text-sm tw-text-affinicar-primary hover:tw-text-affinicar-secondary tw-font-medium tw-transition-colors"
                >
                  Mot de passe oublié ?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="tw-w-full tw-bg-gradient-to-r tw-from-affinicar-primary tw-to-affinicar-secondary tw-text-white tw-font-semibold tw-py-3 tw-px-4 tw-rounded-lg hover:tw-from-affinicar-secondary hover:tw-to-affinicar-dark tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-offset-2 tw-focus:ring-affinicar-primary tw-transition-all tw-duration-200 tw-shadow-md tw-hover:shadow-lg disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-justify-center tw-gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="tw-w-5 tw-h-5 tw-animate-spin" />
                    <span>Connexion en cours...</span>
                  </>
                ) : (
                  <span>Se connecter</span>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

